import React, { useState, useEffect } from 'react';

const API_BASE = (process.env.REACT_APP_API_URL || 'http://localhost:8000') + '/api';

const getAuthHeaders = () => {
  const token = localStorage.getItem('access_token') || localStorage.getItem('token');
  return { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) };
};

const Settings = () => {
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('general');
  const [establishments, setEstablishments] = useState([]);
  const [selectedEstablishment, setSelectedEstablishment] = useState('');
  const [cashierKeys, setCashierKeys] = useState([]);
  const [keyLoading, setKeyLoading] = useState(false);
  const [keysLoading, setKeysLoading] = useState(false);
  const [revealedKeys, setRevealedKeys] = useState({});
  const [copiedField, setCopiedField] = useState(null);
  const [printerSettings, setPrinterSettings] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('printer_settings') || '{}');
    } catch { return {}; }
  });
  const [printerStatus, setPrinterStatus] = useState(null);
  const [printerTesting, setPrinterTesting] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [foundPrinters, setFoundPrinters] = useState([]);
  const [agentStatus, setAgentStatus] = useState(null);

  const [settings, setSettings] = useState({
    // Общие настройки
    restaurant_name: 'PWA Menu',
    restaurant_description: 'Лучший ресторан в городе',
    phone: '+7 (999) 123-45-67',
    email: 'info@pwamenu.ru',
    address: 'г. Москва, ул. Примерная, д. 1',
    working_hours: '10:00 - 22:00',
    
    // Настройки доставки
    delivery_enabled: true,
    delivery_fee: 200,
    free_delivery_threshold: 1000,
    delivery_radius: 10,
    delivery_time: 60,
    
    // Настройки оплаты
    cash_payment: true,
    card_payment: true,
    online_payment: true,
    
    // Уведомления
    email_notifications: true,
    sms_notifications: false,
    push_notifications: true,
    
    // Внешний вид
    primary_color: '#091C36',
    secondary_color: '#FCD66F',
    logo_url: '',
  });

  const tabs = [
    { id: 'general', name: 'Общие', icon: '🏪' },
    { id: 'delivery', name: 'Доставка', icon: '🚚' },
    { id: 'payment', name: 'Оплата', icon: '💳' },
    { id: 'notifications', name: 'Уведомления', icon: '🔔' },
    { id: 'appearance', name: 'Внешний вид', icon: '🎨' },
    { id: 'cashier', name: 'Касса', icon: '🧾' },
    { id: 'printer', name: 'Принтер', icon: '🖨️' },
  ];

  useEffect(() => {
    loadSettings();
    loadEstablishments();
  }, []);

  const loadEstablishments = async () => {
    try {
      const res = await fetch(`${API_BASE}/establishments/admin/list/`, { headers: getAuthHeaders() });
      if (res.ok) {
        const data = await res.json();
        setEstablishments(data);
        if (data.length > 0) {
          setSelectedEstablishment(String(data[0].id));
          loadCashierKeys(data[0].id);
        }
      }
    } catch (e) {
      console.error('Ошибка загрузки заведений:', e);
    }
  };

  const loadCashierKeys = async (estId) => {
    if (!estId) return;
    setKeysLoading(true);
    try {
      const res = await fetch(`${API_BASE}/establishments/${estId}/cashier-keys/`, { headers: getAuthHeaders() });
      if (res.ok) {
        const keys = await res.json();
        setCashierKeys(keys);
        const activeKey = keys.find(k => k.is_active);
        if (activeKey?.key) {
          localStorage.setItem('cashier_api_key', activeKey.key);
          // Автоматически отправляем ключ в агент (чтобы поллинг работал без браузера)
          syncAgentConfig({ cashier_api_key: activeKey.key });
        }
      }
    } catch (e) {
      console.error('Ошибка загрузки ключей:', e);
    } finally {
      setKeysLoading(false);
    }
  };

  const handleGenerateKey = async () => {
    if (!selectedEstablishment) return;
    setKeyLoading(true);
    try {
      const res = await fetch(
        `${API_BASE}/establishments/${selectedEstablishment}/generate-cashier-key/`,
        { method: 'POST', headers: getAuthHeaders() }
      );
      if (res.ok) {
        const newKey = await res.json();
        setCashierKeys(prev => [newKey, ...prev]);
      }
    } catch (e) {
      console.error('Ошибка генерации ключа:', e);
    } finally {
      setKeyLoading(false);
    }
  };

  const handleEstablishmentChange = (id) => {
    setSelectedEstablishment(id);
    setRevealedKeys({});
    setCopiedField(null);
    loadCashierKeys(id);
  };

  const handleCopy = (text, fieldId) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedField(fieldId);
      setTimeout(() => setCopiedField(null), 2000);
    });
  };

  const toggleReveal = (keyId, field) => {
    setRevealedKeys(prev => {
      const composite = `${keyId}-${field}`;
      return { ...prev, [composite]: !prev[composite] };
    });
  };

  const getConnectionCode = (apiKey) => {
    const base = process.env.REACT_APP_API_URL || 'http://localhost:8000';
    return `${base}|${apiKey}`;
  };

  const maskValue = (val) => val ? val.slice(0, 8) + '•'.repeat(Math.min(val.length - 8, 20)) : '';

  const loadSettings = async () => {
    try {
      setLoading(true);
      // В реальном проекте здесь будет вызов API
      // const response = await adminApiService.getEstablishmentSettings();
      // setSettings(response);
    } catch (error) {
      console.error('Ошибка загрузки настроек:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      // В реальном проекте здесь будет вызов API
      // await adminApiService.updateEstablishmentSettings(1, settings);
      alert('Настройки сохранены!');
    } catch (error) {
      console.error('Ошибка сохранения настроек:', error);
      alert('Ошибка сохранения настроек');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setSettings(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const renderGeneralSettings = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Название ресторана
          </label>
          <input
            type="text"
            className="input-field"
            value={settings.restaurant_name}
            onChange={(e) => handleInputChange('restaurant_name', e.target.value)}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Телефон
          </label>
          <input
            type="tel"
            className="input-field"
            value={settings.phone}
            onChange={(e) => handleInputChange('phone', e.target.value)}
          />
        </div>
      </div>
      
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Описание ресторана
        </label>
        <textarea
          rows={3}
          className="input-field"
          value={settings.restaurant_description}
          onChange={(e) => handleInputChange('restaurant_description', e.target.value)}
        />
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Email
          </label>
          <input
            type="email"
            className="input-field"
            value={settings.email}
            onChange={(e) => handleInputChange('email', e.target.value)}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Часы работы
          </label>
          <input
            type="text"
            className="input-field"
            value={settings.working_hours}
            onChange={(e) => handleInputChange('working_hours', e.target.value)}
          />
        </div>
      </div>
      
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Адрес
        </label>
        <input
          type="text"
          className="input-field"
          value={settings.address}
          onChange={(e) => handleInputChange('address', e.target.value)}
        />
      </div>
    </div>
  );

  const renderDeliverySettings = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
        <div>
          <h3 className="font-medium text-gray-900">Включить доставку</h3>
          <p className="text-sm text-gray-600">Разрешить клиентам заказывать доставку</p>
        </div>
        <label className="relative inline-flex items-center cursor-pointer">
          <input
            type="checkbox"
            className="sr-only peer"
            checked={settings.delivery_enabled}
            onChange={(e) => handleInputChange('delivery_enabled', e.target.checked)}
          />
          <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
        </label>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Стоимость доставки (₽)
          </label>
          <input
            type="number"
            min="0"
            className="input-field"
            value={settings.delivery_fee}
            onChange={(e) => handleInputChange('delivery_fee', parseInt(e.target.value))}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Бесплатная доставка от (₽)
          </label>
          <input
            type="number"
            min="0"
            className="input-field"
            value={settings.free_delivery_threshold}
            onChange={(e) => handleInputChange('free_delivery_threshold', parseInt(e.target.value))}
          />
        </div>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Радиус доставки (км)
          </label>
          <input
            type="number"
            min="1"
            max="50"
            className="input-field"
            value={settings.delivery_radius}
            onChange={(e) => handleInputChange('delivery_radius', parseInt(e.target.value))}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Время доставки (мин)
          </label>
          <input
            type="number"
            min="15"
            max="180"
            className="input-field"
            value={settings.delivery_time}
            onChange={(e) => handleInputChange('delivery_time', parseInt(e.target.value))}
          />
        </div>
      </div>
    </div>
  );

  const renderPaymentSettings = () => (
    <div className="space-y-6">
      <h3 className="text-lg font-medium text-gray-900">Способы оплаты</h3>
      
      <div className="space-y-4">
        <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
          <div className="flex items-center space-x-3">
            <span className="text-2xl">💵</span>
            <div>
              <h4 className="font-medium text-gray-900">Наличными курьеру</h4>
              <p className="text-sm text-gray-600">Оплата при получении заказа</p>
            </div>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              className="sr-only peer"
              checked={settings.cash_payment}
              onChange={(e) => handleInputChange('cash_payment', e.target.checked)}
            />
            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
          </label>
        </div>
        
        <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
          <div className="flex items-center space-x-3">
            <span className="text-2xl">💳</span>
            <div>
              <h4 className="font-medium text-gray-900">Картой курьеру</h4>
              <p className="text-sm text-gray-600">Оплата картой при получении</p>
            </div>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              className="sr-only peer"
              checked={settings.card_payment}
              onChange={(e) => handleInputChange('card_payment', e.target.checked)}
            />
            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
          </label>
        </div>
        
        <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
          <div className="flex items-center space-x-3">
            <span className="text-2xl">🌐</span>
            <div>
              <h4 className="font-medium text-gray-900">Онлайн оплата</h4>
              <p className="text-sm text-gray-600">Оплата через интернет</p>
            </div>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              className="sr-only peer"
              checked={settings.online_payment}
              onChange={(e) => handleInputChange('online_payment', e.target.checked)}
            />
            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
          </label>
        </div>
      </div>
    </div>
  );

  const renderNotificationSettings = () => (
    <div className="space-y-6">
      <h3 className="text-lg font-medium text-gray-900">Уведомления</h3>
      
      <div className="space-y-4">
        <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
          <div className="flex items-center space-x-3">
            <span className="text-2xl">📧</span>
            <div>
              <h4 className="font-medium text-gray-900">Email уведомления</h4>
              <p className="text-sm text-gray-600">Получать уведомления на email</p>
            </div>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              className="sr-only peer"
              checked={settings.email_notifications}
              onChange={(e) => handleInputChange('email_notifications', e.target.checked)}
            />
            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
          </label>
        </div>
        
        <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
          <div className="flex items-center space-x-3">
            <span className="text-2xl">📱</span>
            <div>
              <h4 className="font-medium text-gray-900">SMS уведомления</h4>
              <p className="text-sm text-gray-600">Получать SMS о новых заказах</p>
            </div>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              className="sr-only peer"
              checked={settings.sms_notifications}
              onChange={(e) => handleInputChange('sms_notifications', e.target.checked)}
            />
            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
          </label>
        </div>
        
        <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
          <div className="flex items-center space-x-3">
            <span className="text-2xl">🔔</span>
            <div>
              <h4 className="font-medium text-gray-900">Push уведомления</h4>
              <p className="text-sm text-gray-600">Уведомления в браузере</p>
            </div>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              className="sr-only peer"
              checked={settings.push_notifications}
              onChange={(e) => handleInputChange('push_notifications', e.target.checked)}
            />
            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
          </label>
        </div>
      </div>
    </div>
  );

  const renderAppearanceSettings = () => (
    <div className="space-y-6">
      <h3 className="text-lg font-medium text-gray-900">Внешний вид</h3>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Основной цвет
          </label>
          <div className="flex items-center space-x-3">
            <input
              type="color"
              className="w-12 h-12 rounded-lg border border-gray-300"
              value={settings.primary_color}
              onChange={(e) => handleInputChange('primary_color', e.target.value)}
            />
            <input
              type="text"
              className="input-field flex-1"
              value={settings.primary_color}
              onChange={(e) => handleInputChange('primary_color', e.target.value)}
            />
          </div>
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Дополнительный цвет
          </label>
          <div className="flex items-center space-x-3">
            <input
              type="color"
              className="w-12 h-12 rounded-lg border border-gray-300"
              value={settings.secondary_color}
              onChange={(e) => handleInputChange('secondary_color', e.target.value)}
            />
            <input
              type="text"
              className="input-field flex-1"
              value={settings.secondary_color}
              onChange={(e) => handleInputChange('secondary_color', e.target.value)}
            />
          </div>
        </div>
      </div>
      
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Логотип ресторана
        </label>
        <input
          type="file"
          accept="image/*"
          className="input-field"
          onChange={(e) => {
            // Здесь будет логика загрузки файла
            console.log('Загрузка файла:', e.target.files[0]);
          }}
        />
        <p className="text-sm text-gray-500 mt-1">
          Рекомендуемый размер: 200x200px, формат PNG или JPG
        </p>
      </div>
      
      <div className="p-4 bg-gray-50 rounded-lg">
        <h4 className="font-medium text-gray-900 mb-2">Предварительный просмотр</h4>
        <div className="flex items-center space-x-4">
          <div 
            className="w-16 h-16 rounded-lg flex items-center justify-center text-white font-bold"
            style={{ backgroundColor: settings.primary_color }}
          >
            PWA
          </div>
          <div 
            className="px-4 py-2 rounded-lg font-medium"
            style={{ 
              backgroundColor: settings.secondary_color,
              color: settings.primary_color 
            }}
          >
            Кнопка
          </div>
        </div>
      </div>
    </div>
  );

  const AGENT_URL = 'http://localhost:8765';

  const checkAgentAvailable = async () => {
    try {
      const res = await fetch(`${AGENT_URL}/ping`, { signal: AbortSignal.timeout(1500) });
      return res.ok;
    } catch {
      return false;
    }
  };

  // Отправляет конфиг в агент (IP принтера + API ключ) — агент поллит очередь автономно
  const syncAgentConfig = async (patch) => {
    try {
      const agentOk = await checkAgentAvailable();
      if (!agentOk) return;
      await fetch(`${AGENT_URL}/config`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(patch),
      });
    } catch (e) {
      console.warn('Агент недоступен, конфиг не обновлён:', e.message);
    }
  };

  // Загружает статус агента (последний поллинг, конфиг)
  const loadAgentStatus = async () => {
    try {
      const res = await fetch(`${AGENT_URL}/ping`, { signal: AbortSignal.timeout(1500) });
      if (res.ok) {
        const data = await res.json();
        setAgentStatus(data);
      } else {
        setAgentStatus(null);
      }
    } catch {
      setAgentStatus(null);
    }
  };

  const handlePrinterScan = async () => {
    setScanning(true);
    setFoundPrinters([]);
    setPrinterStatus(null);

    const agentOk = await checkAgentAvailable();

    if (!agentOk) {
      setScanning(false);
      setPrinterStatus({
        ok: false,
        msg: 'Агент не запущен. Запусти printer_agent.py на своём Mac (см. инструкцию ниже).',
        agentMissing: true,
      });
      return;
    }

    const currentIp = printerSettings.ip || '192.168.0.1';
    const subnet = currentIp.split('.').slice(0, 3).join('.');
    const port = parseInt(printerSettings.port || 9100);

    try {
      const res = await fetch(`${AGENT_URL}/scan?subnet=${subnet}&port=${port}`);
      const data = await res.json();
      if (data.found && data.found.length > 0) {
        setFoundPrinters(data.found);
        setPrinterStatus({ ok: true, msg: `Найдено ${data.count} устройство(а) на порту ${port}` });
      } else {
        setPrinterStatus({ ok: false, msg: `Принтеры не найдены в сети ${subnet}.0/24 на порту ${port}` });
      }
    } catch (e) {
      setPrinterStatus({ ok: false, msg: `Ошибка сканирования: ${e.message}` });
    } finally {
      setScanning(false);
    }
  };

  const handlePrinterSave = async () => {
    localStorage.setItem('printer_settings', JSON.stringify(printerSettings));
    const ip = printerSettings.ip;
    const port = parseInt(printerSettings.port || 9100);
    if (ip) {
      await syncAgentConfig({ printer_ip: ip, printer_port: port });
    }
    await loadAgentStatus();
    setPrinterStatus({ ok: true, msg: 'Настройки сохранены' + (ip ? ' и отправлены в агент' : '') });
    setTimeout(() => setPrinterStatus(null), 4000);
  };

  const handlePrinterTest = async () => {
    const ip = printerSettings.ip || '192.168.0.194';
    const port = parseInt(printerSettings.port || 9100);
    const width = parseInt(printerSettings.width || 48);
    setPrinterTesting(true);
    setPrinterStatus(null);
    try {
      // Сначала пробуем локальный агент
      const agentOk = await checkAgentAvailable();
      if (agentOk) {
        const res = await fetch(`${AGENT_URL}/test`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ ip, port, width }),
        });
        const data = await res.json();
        if (data.success) {
          setPrinterStatus({ ok: true, msg: `Чек отправлен на ${ip}:${port} (через агент)` });
        } else {
          setPrinterStatus({ ok: false, msg: data.error || 'Ошибка печати' });
        }
      } else {
        setPrinterStatus({
          ok: false,
          msg: 'Агент не запущен. Запусти printer_agent.py на Mac.',
          agentMissing: true,
        });
      }
    } catch (e) {
      setPrinterStatus({ ok: false, msg: `Ошибка: ${e.message}` });
    } finally {
      setPrinterTesting(false);
    }
  };

  const renderPrinterSettings = () => (
    <div className="space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <h3 className="text-lg font-medium text-gray-900">Настройки принтера чеков</h3>
          <p className="text-sm text-gray-500 mt-1">ESC/POS термопринтер по сети (TCP/IP)</p>
        </div>
        <button
          onClick={loadAgentStatus}
          className="text-xs px-3 py-1.5 rounded-lg border border-gray-300 hover:bg-gray-50 transition-colors text-gray-600"
        >
          Статус агента
        </button>
      </div>

      {/* Статус агента */}
      {agentStatus !== null && (
        <div className={`rounded-lg p-3 text-sm space-y-1 ${agentStatus ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
          {agentStatus ? (
            <>
              <div className="flex items-center gap-2 font-medium text-green-800">
                <span className="w-2 h-2 rounded-full bg-green-500 inline-block"></span>
                Агент работает (v{agentStatus.version})
              </div>
              <div className="text-green-700 text-xs">
                Принтер: {agentStatus.printer_configured ? '✓ настроен' : '✗ не задан'} &nbsp;|&nbsp;
                API ключ: {agentStatus.api_key_configured ? '✓ задан' : '✗ не задан'} &nbsp;|&nbsp;
                Поллинг: {agentStatus.polling_enabled ? 'включён' : 'выключен'}
              </div>
              {agentStatus.last_poll && (
                <div className="text-green-600 text-xs">Последний опрос: {agentStatus.last_poll}</div>
              )}
            </>
          ) : (
            <div className="text-red-700 font-medium">✗ Агент недоступен (localhost:8765)</div>
          )}
        </div>
      )}

      {/* Статус */}
      {printerStatus && (
        <div className={`flex items-center gap-2 px-4 py-3 rounded-lg text-sm font-medium ${
          printerStatus.ok ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-700 border border-red-200'
        }`}>
          <span>{printerStatus.ok ? '✓' : '✗'}</span>
          {printerStatus.msg}
        </div>
      )}

      {/* Текущий принтер */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex items-center gap-4">
        <div className="text-3xl">🖨️</div>
        <div>
          <div className="font-semibold text-blue-900">
            {printerSettings.ip || '—'}:{printerSettings.port || 9100}
          </div>
          <div className="text-sm text-blue-600">{printerSettings.name || 'Термопринтер (ESC/POS)'}</div>
        </div>
        <div className="ml-auto">
          <span className={`text-xs px-2 py-1 rounded-full font-medium ${printerSettings.ip ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
            {printerSettings.ip ? 'Настроен' : 'Не настроен'}
          </span>
        </div>
      </div>

      {/* Автопоиск принтера через локальный агент */}
      <div className="border border-gray-200 rounded-xl p-4 space-y-3">
        <div className="flex items-center justify-between">
          <div>
            <div className="font-medium text-gray-800">Автоматический поиск принтера</div>
            <div className="text-xs text-gray-500 mt-0.5">
              Сканирует локальную сеть через агент на этом Mac
            </div>
          </div>
          <button
            onClick={handlePrinterScan}
            disabled={scanning}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              scanning
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                : 'bg-indigo-600 text-white hover:bg-indigo-700 active:scale-95'
            }`}
          >
            {scanning ? (
              <>
                <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/>
                </svg>
                Сканирую...
              </>
            ) : (
              <>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z"/>
                </svg>
                Найти принтер
              </>
            )}
          </button>
        </div>

        {/* Инструкция если агент не запущен */}
        {printerStatus?.agentMissing && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 space-y-2">
            <div className="text-xs font-semibold text-amber-800">Как запустить агент:</div>
            <div className="text-xs text-amber-700 space-y-1">
              <div>1. Открой Терминал на Mac</div>
              <div>2. Выполни команду:</div>
              <code className="block bg-amber-100 rounded px-2 py-1 font-mono text-amber-900 text-xs select-all">
                python3 /Users/rustamradzhabov/Desktop/BurgerZona/printer-agent/printer_agent.py
              </code>
              <div className="mt-1 text-amber-600">Для автозапуска при входе в систему:</div>
              <code className="block bg-amber-100 rounded px-2 py-1 font-mono text-amber-900 text-xs select-all">
                cp /Users/rustamradzhabov/Desktop/BurgerZona/printer-agent/com.burgerzzona.printer-agent.plist ~/Library/LaunchAgents/ && launchctl load ~/Library/LaunchAgents/com.burgerzzona.printer-agent.plist
              </code>
            </div>
          </div>
        )}

        {/* Найденные принтеры */}
        {foundPrinters.length > 0 && (
          <div className="space-y-2">
            <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Найдено на порту {printerSettings.port || 9100}:</div>
            {foundPrinters.map((p) => (
              <div key={p.ip} className="flex items-center gap-3 bg-white border border-gray-200 rounded-lg px-3 py-2">
                <span className="text-lg">🖨️</span>
                <div className="flex-1">
                  <div className="font-mono text-sm font-semibold text-gray-800">{p.ip}:{p.port}</div>
                  {p.hostname && <div className="text-xs text-gray-400">{p.hostname}</div>}
                </div>
                <button
                  onClick={() => {
                    setPrinterSettings(prev => ({ ...prev, ip: p.ip, port: p.port }));
                    localStorage.setItem('printer_settings', JSON.stringify({ ...printerSettings, ip: p.ip, port: p.port }));
                    setPrinterStatus({ ok: true, msg: `Принтер ${p.ip}:${p.port} выбран и сохранён` });
                    setTimeout(() => setPrinterStatus(null), 3000);
                  }}
                  className="px-3 py-1 bg-green-500 text-white text-xs font-medium rounded-md hover:bg-green-600 active:scale-95 transition-all"
                >
                  Выбрать
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Название принтера</label>
          <input
            type="text"
            className="input-field"
            placeholder="Термопринтер BurgerZona"
            value={printerSettings.name || ''}
            onChange={e => setPrinterSettings(p => ({ ...p, name: e.target.value }))}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Протокол</label>
          <select
            className="input-field"
            value={printerSettings.protocol || 'tcp'}
            onChange={e => setPrinterSettings(p => ({ ...p, protocol: e.target.value }))}
          >
            <option value="tcp">TCP/IP (ESC/POS)</option>
            <option value="usb">USB</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            IP-адрес принтера
          </label>
          <input
            type="text"
            className="input-field font-mono"
            placeholder="192.168.0.194"
            value={printerSettings.ip || '192.168.0.194'}
            onChange={e => setPrinterSettings(p => ({ ...p, ip: e.target.value }))}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Порт
          </label>
          <input
            type="number"
            className="input-field font-mono"
            placeholder="9100"
            value={printerSettings.port || 9100}
            onChange={e => setPrinterSettings(p => ({ ...p, port: parseInt(e.target.value) || 9100 }))}
          />
          <p className="text-xs text-gray-400 mt-1">Стандартный порт ESC/POS — 9100</p>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Ширина чека (символов)</label>
          <select
            className="input-field"
            value={printerSettings.width || 32}
            onChange={e => setPrinterSettings(p => ({ ...p, width: parseInt(e.target.value) }))}
          >
            <option value={32}>32 символа (58мм)</option>
            <option value={48}>48 символов (80мм)</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Кодировка</label>
          <select
            className="input-field"
            value={printerSettings.encoding || 'cp866'}
            onChange={e => setPrinterSettings(p => ({ ...p, encoding: e.target.value }))}
          >
            <option value="cp866">CP866 (DOS Cyrillic)</option>
            <option value="cp1251">CP1251 (Windows)</option>
            <option value="utf8">UTF-8</option>
          </select>
        </div>
      </div>

      {/* Настройки чека */}
      <div className="border-t border-gray-200 pt-5">
        <h4 className="font-medium text-gray-900 mb-4">Настройки чека</h4>
        <div className="space-y-3">
          {[
            { key: 'print_logo', label: 'Печатать название заведения', def: true },
            { key: 'print_address', label: 'Печатать адрес', def: false },
            { key: 'print_cashier', label: 'Печатать имя кассира', def: true },
            { key: 'auto_cut', label: 'Автообрезка бумаги', def: true },
            { key: 'open_drawer', label: 'Открывать денежный ящик', def: false },
          ].map(({ key, label, def }) => (
            <div key={key} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <span className="text-sm text-gray-700">{label}</span>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  className="sr-only peer"
                  checked={printerSettings[key] !== undefined ? printerSettings[key] : def}
                  onChange={e => setPrinterSettings(p => ({ ...p, [key]: e.target.checked }))}
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
              </label>
            </div>
          ))}
        </div>
      </div>

      <div className="flex gap-3 pt-2">
        <button
          onClick={handlePrinterTest}
          disabled={printerTesting}
          className="px-5 py-2.5 bg-gray-800 text-white rounded-lg hover:bg-gray-700 transition-colors font-medium text-sm disabled:opacity-50"
        >
          {printerTesting ? 'Печать...' : '🖨️ Напечатать тест'}
        </button>
        <button
          onClick={handlePrinterSave}
          className="btn-primary"
        >
          Сохранить настройки
        </button>
      </div>
    </div>
  );

  const renderCashierSettings = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium text-gray-900">Подключение кассы (Flutter)</h3>
        <p className="text-sm text-gray-500 mt-1">
          Сгенерируйте ключ — заказчик вводит <strong>connection code</strong> в приложении.
        </p>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Заведение</label>
        <select
          className="input-field"
          value={selectedEstablishment}
          onChange={(e) => handleEstablishmentChange(e.target.value)}
        >
          {establishments.length === 0 && <option value="">— нет заведений —</option>}
          {establishments.map((est) => (
            <option key={est.id} value={String(est.id)}>{est.name}</option>
          ))}
        </select>
      </div>

      <button
        onClick={handleGenerateKey}
        disabled={keyLoading || !selectedEstablishment}
        className="btn-primary"
      >
        {keyLoading ? 'Генерация...' : 'Сгенерировать API ключ'}
      </button>

      {/* Сохранённые ключи */}
      <div className="border border-gray-200 rounded-xl overflow-hidden">
        <div className="flex items-start justify-between px-5 py-4 bg-white border-b border-gray-100">
          <div>
            <p className="font-medium text-gray-900">Сохранённые ключи</p>
            <p className="text-sm text-gray-500 mt-0.5">Эти ключи сохраняются и доступны после перезагрузки страницы</p>
          </div>
          <button
            onClick={() => loadCashierKeys(selectedEstablishment)}
            disabled={keysLoading}
            className="text-sm px-3 py-1.5 rounded-lg border border-gray-300 hover:bg-gray-50 transition-colors text-gray-700 shrink-0 mt-0.5"
          >
            {keysLoading ? '...' : 'Обновить'}
          </button>
        </div>

        {cashierKeys.length === 0 ? (
          <p className="text-sm text-gray-400 text-center py-8">
            {keysLoading ? 'Загрузка...' : 'Ключей нет — сгенерируйте первый'}
          </p>
        ) : (
          <div className="divide-y divide-gray-100">
            {cashierKeys.map((k) => {
              const connCode = getConnectionCode(k.key);
              const keyRevealed = revealedKeys[`${k.id}-key`];
              const codeRevealed = revealedKeys[`${k.id}-code`];
              const shortName = `Ключ • ${k.key.slice(0, 8)}...`;
              const createdAt = new Date(k.created_at).toLocaleString('ru-RU');
              return (
                <div key={k.id} className="px-5 py-4 bg-white">
                  {/* Заголовок ключа */}
                  <div className="flex items-center gap-2 mb-3">
                    <span className="font-medium text-gray-900 text-sm">{shortName}</span>
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${k.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                      {k.is_active ? 'Активен' : 'Неактивен'}
                    </span>
                    <span className="text-xs text-gray-400 ml-auto">создан: {createdAt}</span>
                  </div>

                  {/* API key + Connection code */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {/* API key */}
                    <div className="border border-gray-200 rounded-lg overflow-hidden">
                      <div className="flex items-center justify-between px-3 py-2 bg-gray-50 border-b border-gray-200">
                        <span className="text-xs font-medium text-gray-600">API key</span>
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => toggleReveal(k.id, 'key')}
                            className="text-xs px-2 py-0.5 rounded border border-gray-300 bg-white hover:bg-gray-100 transition-colors"
                            title={keyRevealed ? 'Скрыть' : 'Показать'}
                          >
                            {keyRevealed ? '🙈' : '👁'}
                          </button>
                          <button
                            onClick={() => handleCopy(k.key, `${k.id}-key`)}
                            className="text-xs px-2 py-0.5 rounded border border-gray-300 bg-white hover:bg-gray-100 transition-colors"
                          >
                            {copiedField === `${k.id}-key` ? '✓' : 'Копировать'}
                          </button>
                        </div>
                      </div>
                      <div className="px-3 py-2">
                        <code className="text-xs font-mono text-gray-800 break-all">
                          {keyRevealed ? k.key : maskValue(k.key)}
                        </code>
                      </div>
                    </div>

                    {/* Connection code */}
                    <div className="border border-gray-200 rounded-lg overflow-hidden">
                      <div className="flex items-center justify-between px-3 py-2 bg-gray-50 border-b border-gray-200">
                        <span className="text-xs font-medium text-gray-600">Connection code</span>
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => toggleReveal(k.id, 'code')}
                            className="text-xs px-2 py-0.5 rounded border border-gray-300 bg-white hover:bg-gray-100 transition-colors"
                            title={codeRevealed ? 'Скрыть' : 'Показать'}
                          >
                            {codeRevealed ? '🙈' : '👁'}
                          </button>
                          <button
                            onClick={() => handleCopy(connCode, `${k.id}-code`)}
                            className="text-xs px-2 py-0.5 rounded border border-gray-300 bg-white hover:bg-gray-100 transition-colors"
                          >
                            {copiedField === `${k.id}-code` ? '✓' : 'Копировать'}
                          </button>
                        </div>
                      </div>
                      <div className="px-3 py-2">
                        <code className="text-xs font-mono text-gray-800 break-all">
                          {codeRevealed ? connCode : maskValue(connCode)}
                        </code>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );

  const renderTabContent = () => {
    switch (activeTab) {
      case 'general':
        return renderGeneralSettings();
      case 'delivery':
        return renderDeliverySettings();
      case 'payment':
        return renderPaymentSettings();
      case 'notifications':
        return renderNotificationSettings();
      case 'appearance':
        return renderAppearanceSettings();
      case 'cashier':
        return renderCashierSettings();
      case 'printer':
        return renderPrinterSettings();
      default:
        return renderGeneralSettings();
    }
  };

  return (
    <div className="space-y-6">
      {/* Заголовок */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Настройки</h1>
          <p className="text-gray-600 mt-1">Управляйте настройками ресторана</p>
        </div>
        <button
          onClick={handleSave}
          disabled={loading}
          className="btn-primary"
        >
          {loading ? 'Сохранение...' : 'Сохранить изменения'}
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Боковое меню */}
        <div className="lg:col-span-1">
          <nav className="space-y-1">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`w-full flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                  activeTab === tab.id
                    ? 'bg-primary-500 text-white'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                }`}
              >
                <span className="mr-3 text-lg">{tab.icon}</span>
                {tab.name}
              </button>
            ))}
          </nav>
        </div>

        {/* Основной контент */}
        <div className="lg:col-span-3">
          <div className="card">
            {renderTabContent()}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Settings; 